import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { useInView } from "react-intersection-observer";
import { formatDate } from "utils";
import { TimelineEvent } from "components/Timeline";
import Card from "components/Card";
import { H3 } from "components/Text";
import { colors, fontSize } from "style";
import { TIMELINE_EVENT_TYPES, TIMELINE_EVENTS } from "constants/index";
import { TextLoader } from "../Loader";
import { Grid } from "components/Containers";
import { Header } from "components/DisplayBox";
import { css } from "@emotion/react";

/**
 * TimelineList
 *
 * @param {String}   id
 * @param {Object}   user
 * @param {Boolean}  loading
 * @param {Array}    data
 */
const TimelineList = ({ id, user, loading, data }) => {
  const { ref } = useInView();

  return (
    <>
      <Grid>
        {data.map(({ date, items }) => {
          const hasChildren = items.some(
            ({ key, eventType }) =>
              Object.values(TIMELINE_EVENTS).includes(key) || Object.values(TIMELINE_EVENT_TYPES).includes(eventType)
          );

          if (!hasChildren) return;

          return (
            <Fragment key={date}>
              <Grid.col start={1} end={13}>
                <StyledCard>
                  <Header>
                    <H3>{formatDate(date)}</H3>
                  </Header>
                  {items.map((data) => (
                    <TimelineEvent key={data.uuid} data={data} user={user} profileId={id} />
                  ))}
                </StyledCard>
              </Grid.col>
            </Fragment>
          );
        })}
        <div ref={ref} />
      </Grid>
      {loading && (
        <Grid>
          {getSequentialArrayWithRandomSize().map((i) => (
            <TimelineLoader key={i} />
          ))}
        </Grid>
      )}
    </>
  );
};

const TimelineLoader = () => (
  <Grid.col start={1} end={13}>
    <StyledCard>
      <Header>
        <TextLoader width="30%" />
      </Header>
      {getSequentialArrayWithRandomSize().map((i) => (
        <div css={styles.loading_header} key={i}>
          <div css={styles.loading_icon}>
            <TextLoader width="10%" />
          </div>
          <div css={styles.loading_text}>
            <TextLoader width="90%" />
          </div>
        </div>
      ))}
    </StyledCard>
  </Grid.col>
);

function getSequentialArrayWithRandomSize(min = 1, max = 10) {
  const size = Math.floor(Math.random() * (max - min + 1)) + min;
  return Array.from({ length: size }, (_, i) => i + 1);
}

const StyledCard = styled(Card)`
  padding: 3rem 3rem 0 3rem;
  font-size: ${fontSize.small};
  line-height: normal;
  color: ${colors.grayAnatomyLight1};
`;

const styles = {
  loading_header: css`
    display: flex;
    gap: 2rem;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
  `,
  loading_text: css`
    padding-left: 1rem;
    display: contents;
  `,
  loading_icon: css`
    display: contents;
  `,
};

TimelineList.propTypes = {
  id: PropTypes.string.isRequired,
  user: PropTypes.object,
  loading: PropTypes.bool,
  data: PropTypes.object.isRequired,
};

export default TimelineList;
